import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Box,
  Stack
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import { useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { clearAndonWorkstationsAndGrids } from "~/features/andon/andon.slice";

import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { updateUsersFulfillmentCenter } from "~/redux/actions/store";
import { setSiteFcId, setThisWorkstation } from "~/redux/actions/workstations";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";

import { warehouseApi } from "~/redux/warehouse/warehouseApi";
import { Guid } from "~/types/api";

/**
 * Renders a dropdown to set the device's fulfillment center from a list of all
 * fulfillment centers belonging to an organization.
 */
export const FulfillmentCenterSelectOld = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const [selectedFulfillmentCenterId, setSelectedFulfillmentCenterId] =
    useState<Guid | null>(null);
  const fulfillmentCenterId = useAppSelector((state) =>
    state.workstations.siteFulfillmentCenterId
      ? state.workstations.siteFulfillmentCenterId
      : state.store.usersFulfillmentCenter
        ? state.store.usersFulfillmentCenter.fulfillmentCenterId
        : null
  );
  useEffect(() => {
    setSelectedFulfillmentCenterId(fulfillmentCenterId);
  }, [fulfillmentCenterId]);
  const {
    isFetching: fulfillmentCentersFetching,
    data: fulfillmentCenters,
    error: fulfillmentCentersFetchError,
    refetch: refetchFulfillmentCenters
  } = useGetAllFulfillmentCentersQuery();

  const handleSelectionSelected = async () => {
    if (!selectedFulfillmentCenterId) {
      return;
    }
    try {
      const selectedFc = fulfillmentCenters?.find(
        (fc) => fc.fulfillmentCenterId === selectedFulfillmentCenterId
      );
      dispatch(
        setSiteFcId(selectedFulfillmentCenterId, selectedFc?.abbreviation)
      );
      dispatch(clearAndonWorkstationsAndGrids());
      await dispatch(setThisWorkstation(null, true));
      await dispatch(updateUsersFulfillmentCenter(selectedFulfillmentCenterId));

      dispatch(
        warehouseApi.util.invalidateTags([
          "autostore grid",
          "workstation",
          "bin configuration"
        ])
      );
      const fcParam = selectedFc?.abbreviation
        ? `?fc=${encodeURIComponent(selectedFc.abbreviation)}`
        : "";
      window.location.href = `${window.location.pathname}${fcParam}`;
    } catch (err) {
      errorToast(getMessageFromRtkError(err));
    }
  };

  if (fulfillmentCentersFetching) {
    return (
      <Box sx={{ width: "100%" }}>
        <Skeleton variant="rectangular" />
      </Box>
    );
  }

  if (fulfillmentCentersFetchError) {
    return (
      <Alert
        variant="outlined"
        severity="error"
        action={
          <Button onClick={refetchFulfillmentCenters}>{t("retry")}</Button>
        }
      >
        {getMessageFromRtkError(fulfillmentCentersFetchError)}
      </Alert>
    );
  }

  return (
    <>
      {fulfillmentCenters?.length && (
        <Stack direction="row" spacing={2} alignItems="baseline">
          <FormControl
            sx={{ maxWidth: "sm" }}
            aria-label="fulfillment-center"
            id="fulfillment-center"
          >
            <Select
              labelId="fulfillment-center"
              size="small"
              variant="outlined"
              aria-label="fulfillment-center-select-label"
              value={
                fulfillmentCenters?.find(
                  (fc) => fc.fulfillmentCenterId === selectedFulfillmentCenterId
                )
                  ? selectedFulfillmentCenterId
                  : ""
              }
            >
              {fulfillmentCenters.map((fc) => (
                <MenuItem
                  key={fc.fulfillmentCenterId}
                  value={fc.fulfillmentCenterId}
                  onClick={() =>
                    setSelectedFulfillmentCenterId(fc.fulfillmentCenterId)
                  }
                >
                  {fc.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{t("select fc label")}</FormHelperText>
          </FormControl>
          <Button
            variant="contained"
            color="secondary"
            disabled={!selectedFulfillmentCenterId}
            onClick={() => handleSelectionSelected()}
            aria-label="assign-fulfillment-center"
          >
            {t("assign")}
          </Button>
        </Stack>
      )}
    </>
  );
};
